import { Injectable, inject, signal } from '@angular/core';
import { LogService } from './log.service';

/**
 * Servicio de monitoreo de conectividad de red.
 *
 * Detecta cambios en el estado de conexión a internet usando la API nativa
 * del navegador y expone el estado como signal reactivo.
 *
 * @example
 * ```typescript
 * const network = inject(NetworkService);
 *
 * // Usar en template
 * @if (network.isOnline()) {
 *   <div>Conectado</div>
 * } @else {
 *   <div>Sin conexión</div>
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class NetworkService {
  private readonly logger = inject(LogService);
  /**
   * Estado actual de la conexión a internet
   * true = online, false = offline
   */
  readonly isOnline = signal<boolean>(navigator.onLine);

  /**
   * Inverso del estado de conexión (para facilitar uso en templates)
   */
  readonly isOffline = signal<boolean>(!navigator.onLine);

  constructor() {
    this.initListeners();
  }

  /**
   * Inicializa los listeners de eventos de red del navegador
   */
  private initListeners(): void {
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
  }

  /**
   * Maneja el evento cuando se detecta conexión
   */
  private handleOnline(): void {
    this.isOnline.set(true);
    this.isOffline.set(false);
    this.logger.info('[NetworkService] Conexión detectada');
  }

  /**
   * Maneja el evento cuando se pierde la conexión
   */
  private handleOffline(): void {
    this.isOnline.set(false);
    this.isOffline.set(true);
    this.logger.warn('[NetworkService] Conexión perdida');
  }

  /**
   * Verifica manualmente el estado de la conexión
   * usando navigator.onLine
   */
  checkConnection(): boolean {
    const online = navigator.onLine;
    this.isOnline.set(online);
    this.isOffline.set(!online);
    return online;
  }
}
